<template>
  <div>
    <b-card class="mb-1">
      <b-row class="align-items-end">
        <b-col
          cols="12"
          md="9"
        >
          <b-form-group
            :label="$t('partner-admin.users.assign-user')"
            class="mb-1 mb-md-0"
          >
            <v-select
              v-model="selectedUserId"
              :options="assignableUserOptions"
              :reduce="option => option.id"
              label="label"
              :placeholder="$t('partner-admin.users.assign-user-placeholder')"
              :disabled="loading || actionLoading"
              @search="handleAssignableSearch"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="3"
        >
          <b-button
            variant="primary"
            block
            :disabled="loading || actionLoading || !selectedUserId"
            @click="assignUser"
          >
            {{ $t('partner-admin.users.assign-action') }}
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <b-card
      no-body
      class="mb-0"
    >
      <div class="p-1">
        <b-row class="align-items-end">
          <b-col
            cols="12"
            md="2"
            class="d-flex align-items-end"
          >
            <b-form-group
              :label="$t('partner-admin.filters.limit')"
              class="mb-1 mb-md-0 w-100"
            >
              <b-form-select
                v-model="query.limit"
                :options="limitOptions"
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="4"
            class="ml-auto"
          >
            <b-form-group
              :label="$t('partner-admin.filters.search')"
              class="mb-1 mb-md-0 w-100"
            >
              <b-form-input v-model="query.q" />
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <rc-overlay :show="loading || listLoading || actionLoading">
        <b-table
          :items="assignedUsers"
          :fields="fields"
          responsive
          show-empty
          :empty-text="(loading || listLoading) ? $t('shared.loading') : $t('shared.no-data')"
        >
          <template #cell(fullName)="data">
            <b-link
              class="text-nowrap"
              :to="{ name: 'user-edit', params: { id: data.item.id } }"
            >
              {{ formatUserFullName(data.item) }}
            </b-link>
          </template>

          <template #cell(roles)="data">
            <div class="d-flex flex-wrap gap-1">
              <b-badge
                v-for="role in data.item.roles"
                :key="`${data.item.id}-${role}`"
                variant="light-primary"
              >
                {{ resolveRoleLabel(role) }}
              </b-badge>
            </div>
          </template>

          <template #cell(status)="data">
            <b-badge :variant="data.item.status ? 'success' : 'secondary'">
              {{ data.item.status ? $t('partner-admin.users.active') : $t('partner-admin.users.inactive') }}
            </b-badge>
          </template>

          <template #cell(actions)="data">
            <b-button
              size="sm"
              variant="outline-danger"
              :disabled="loading || actionLoading"
              @click="unassignUser(data.item.id)"
            >
              {{ $t('partner-admin.users.unassign-action') }}
            </b-button>
          </template>
        </b-table>
      </rc-overlay>

      <table-bottom
        :query="query"
        :total="total"
      />
    </b-card>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BCol,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BLink,
  BRow,
  BTable,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import RcOverlay from '@/layouts/components/rc/RcOverlay.vue'
import TableBottom from '@/layouts/components/table/TableBottom.vue'
import { parseRequestError } from '@/helpers/helpers'

export default {
  name: 'ExternalPartnerUsersSection',
  components: {
    BBadge,
    BButton,
    BCard,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BLink,
    BRow,
    BTable,
    vSelect,
    RcOverlay,
    TableBottom,
  },
  props: {
    partnerId: {
      type: Number,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedUserId: null,
      actionLoading: false,
      listLoading: false,
      query: {
        q: null,
        limit: 25,
        page: 1,
        sortField: 'id',
        sortDir: 'ASC',
      },
      limitOptions: [10, 25, 50, 100].map(limit => ({ value: limit, text: limit })),
    }
  },
  computed: {
    assignedUsers() {
      return this.$store.getters['externalPartner/getAssignedUsers']
    },
    total() {
      return this.$store.getters['externalPartner/getAssignedUsersTotal']
    },
    assignableUsers() {
      return this.$store.getters['externalPartner/getAssignableUsers']
    },
    assignableUserOptions() {
      return this.assignableUsers.map(user => ({
        id: user.id,
        label: `${this.formatUserFullName(user)} (${user.username || user.id})`,
      }))
    },
    fields() {
      return [
        { key: 'fullName', label: this.$t('partner-admin.users.fields.name') },
        { key: 'username', label: this.$t('partner-admin.users.fields.username') },
        { key: 'roles', label: this.$t('partner-admin.users.fields.roles') },
        { key: 'status', label: this.$t('partner-admin.users.fields.status') },
        { key: 'actions', label: this.$t('shared.actions') },
      ]
    },
  },
  watch: {
    partnerId: {
      immediate: true,
      handler() {
        this.initializeSection()
      },
    },
    query: {
      deep: true,
      handler() {
        this.fetchAssignedUsersList()
      },
    },
  },
  methods: {
    formatUserFullName(user) {
      const parts = [user?.name, user?.surname].filter(Boolean)
      if (parts.length > 0) {
        return parts.join(' ')
      }

      return user?.username || '-'
    },
    resolveRoleLabel(role) {
      const i18nKey = `roles.${role}`
      if (typeof this.$te === 'function' && this.$te(i18nKey)) {
        return this.$t(i18nKey)
      }

      return role
    },
    initializeSection() {
      this.query.page = 1
      this.fetchAssignedUsersList()
      this.fetchAssignableUsers()
    },
    fetchAssignedUsersList() {
      if (!this.partnerId) {
        return
      }

      this.listLoading = true

      const payload = {
        partnerId: this.partnerId,
        query: {
          limit: this.query.limit,
          page: this.query.page,
          sortField: this.query.sortField,
          sortDir: this.query.sortDir,
        },
      }

      if (this.query.q) {
        payload.query.q = this.query.q
      }

      this.$store.dispatch('externalPartner/fetchAssignedUsers', payload)
        .catch(error => {
          parseRequestError(error)
          window.toast.notify.danger(this.$t('partner-admin.errors.fetch-users'))
        })
        .finally(() => {
          this.listLoading = false
        })
    },
    fetchAssignableUsers(q = null) {
      const query = {
        onlyUnassigned: true,
        limit: 100,
        page: 1,
        sortField: 'id',
        sortDir: 'ASC',
      }

      if (q) {
        query.q = q
      }

      this.$store.dispatch('externalPartner/fetchAssignableUsers', query)
        .catch(error => {
          parseRequestError(error)
          window.toast.notify.danger(this.$t('partner-admin.errors.fetch-users'))
        })
    },
    handleAssignableSearch(searchText) {
      this.fetchAssignableUsers(searchText)
    },
    async assignUser() {
      this.actionLoading = true

      try {
        await this.$store.dispatch('externalPartner/assignUserToPartner', {
          partnerId: this.partnerId,
          data: {
            userId: this.selectedUserId,
          },
        })
        this.selectedUserId = null
        this.fetchAssignableUsers()
        this.fetchAssignedUsersList()
      } catch (error) {
        parseRequestError(error)
        window.toast.notify.danger(this.$t('partner-admin.errors.assign-user'))
      } finally {
        this.actionLoading = false
      }
    },
    async unassignUser(userId) {
      this.actionLoading = true

      try {
        await this.$store.dispatch('externalPartner/unassignUserFromPartner', {
          partnerId: this.partnerId,
          userId,
        })
        this.fetchAssignableUsers()
        this.fetchAssignedUsersList()
      } catch (error) {
        parseRequestError(error)
        window.toast.notify.danger(this.$t('partner-admin.errors.unassign-user'))
      } finally {
        this.actionLoading = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.gap-1 {
  gap: 0.5rem;
}
</style>
